import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ACHIEVEMENT_META } from "@/lib/achievements";

/**
 * Polls /api/achievements/mine periodically and shows a toast for newly unlocked achievements.
 * Uses localStorage to persist which have been seen across sessions.
 */
const SEEN_KEY = "fb27_achievements_seen";

function loadSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set(); }
}
function saveSeen(set) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set))); } catch {}
}

export default function AchievementWatcher() {
  const { user } = useAuth();
  const seen = useRef(loadSeen());
  const { data } = useQuery({
    queryKey: ["my-achievements-watch", user?.user_id],
    queryFn: () => api.get("/achievements/mine").then(r => r.data),
    enabled: !!user,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!data || !user) return;
    data.forEach(a => {
      if (seen.current.has(a.achievement_id)) return;
      seen.current.add(a.achievement_id);
      const meta = ACHIEVEMENT_META[a.achievement_id] || {};
      toast(
        <div className="flex items-center gap-3" data-testid={`ach-toast-${a.achievement_id}`}>
          <div className="text-3xl">{a.icon || meta.icon}</div>
          <div>
            <div className="text-xs text-orange-400 uppercase tracking-widest">🏆 Achievement låst op</div>
            <div className="font-bold text-lg">{a.name || meta.name}</div>
            <div className="text-sm text-zinc-400">{a.description}</div>
          </div>
        </div>,
        { duration: 6000 }
      );
    });
    saveSeen(seen.current);
  }, [data, user]);

  return null;
}
